import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, formatEUR } from "../lib/api";
import { CheckCircle2, Phone, MapPin, Loader2, Snowflake, Sun, Moon } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";

const CourierSchedule = () => {
  const { date } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [acting, setActing] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/courier/schedule/${date}?token=${encodeURIComponent(token || "")}`);
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Accesso non autorizzato");
    }
  };

  useEffect(() => {
    if (!token) { setError("Link non valido"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, token]);

  const markDelivered = async (order_id) => {
    setActing(order_id);
    try {
      await api.post(`/courier/schedule/${date}/orders/${order_id}/delivered?token=${encodeURIComponent(token)}`);
      toast.success("Consegna confermata");
      load();
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail || "Riprova." });
    } finally { setActing(null); }
  };

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center p-6" data-testid="courier-error">
        <div className="text-center">
          <div className="w-14 h-14 rounded-2xl bg-red-50 grid place-items-center mx-auto mb-4">
            <Snowflake className="w-6 h-6 text-red-500" />
          </div>
          <p className="font-heading text-2xl text-[#032B44]">Link non valido</p>
          <p className="text-sm text-slate-600 mt-2">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center" data-testid="courier-loading">
        <Loader2 className="w-6 h-6 animate-spin text-sky-600" />
      </div>
    );
  }

  const mattina = data.orders.filter((o) => o.delivery_slot === "mattina");
  const pomeriggio = data.orders.filter((o) => o.delivery_slot === "pomeriggio");
  const parsed = new Date(date + "T00:00:00");

  return (
    <div className="min-h-screen bg-sky-50/40 pb-20" data-testid="courier-page">
      <header className="bg-[#032B44] text-white px-5 py-6 sticky top-0 z-10 shadow-lg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 grid place-items-center">
            <Snowflake className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-sky-200">EasyIce · Corriere</p>
            <h1 className="font-heading text-xl font-medium capitalize">
              {format(parsed, "EEEE d MMMM", { locale: it })}
            </h1>
          </div>
        </div>
        <p className="text-xs text-sky-200 mt-3">
          {data.orders.length} consegne · {mattina.length} mattina · {pomeriggio.length} pomeriggio
        </p>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-8">
        <SlotSection title="Mattina" time="08:00 – 12:30" icon={<Sun />} orders={mattina} acting={acting} onDelivered={markDelivered} />
        <SlotSection title="Pomeriggio" time="15:00 – 19:00" icon={<Moon />} orders={pomeriggio} acting={acting} onDelivered={markDelivered} />
        {data.orders.length === 0 && (
          <div className="bg-white border border-sky-100 rounded-2xl p-10 text-center text-sm text-slate-500">
            Nessuna consegna oggi.
          </div>
        )}
      </main>
    </div>
  );
};

const SlotSection = ({ title, time, icon, orders, acting, onDelivered }) => {
  if (orders.length === 0) return null;
  return (
    <section>
      <div className="flex items-center gap-2 mb-3 px-1">
        {React.cloneElement(icon, { className: "w-4 h-4 text-sky-700" })}
        <h2 className="font-heading text-lg font-medium text-[#032B44]">{title}</h2>
        <span className="text-xs text-slate-500">· {time}</span>
        <span className="ml-auto text-xs text-slate-500">{orders.length}</span>
      </div>
      <div className="space-y-3">
        {orders.map((o) => <OrderCard key={o.order_id} order={o} acting={acting === o.order_id} onDelivered={onDelivered} />)}
      </div>
    </section>
  );
};

const OrderCard = ({ order, acting, onDelivered }) => {
  const delivered = !!order.delivered_at;
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.delivery_address || "")}`;
  const telUrl = order.user_phone ? `tel:${order.user_phone.replace(/\s+/g, "")}` : null;
  const isCash = order.payment_method === "cash";

  return (
    <article className={`bg-white rounded-2xl p-5 border transition-all ${delivered ? "border-emerald-200 opacity-70" : "border-sky-100"}`} data-testid={`courier-order-${order.order_id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <p className="font-heading text-base font-medium text-[#032B44] truncate">{order.user_name}</p>
          <p className="text-xs text-slate-500 font-mono">#{order.order_id.slice(-6)}</p>
        </div>
        <div className="text-right">
          <p className="font-heading text-xl font-semibold text-[#032B44]">{formatEUR(order.total)}</p>
          {isCash ? (
            <span className="text-[10px] tracking-wider uppercase font-bold px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
              Incassa contanti
            </span>
          ) : (
            <span className="text-[10px] tracking-wider uppercase font-bold px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
              Già pagato
            </span>
          )}
        </div>
      </div>

      <ul className="text-xs text-slate-600 mb-3 space-y-0.5">
        {order.items.map((it) => <li key={`${order.order_id}-${it.product_id}`}>· {it.name} × {it.quantity}</li>)}
      </ul>

      <div className="space-y-2">
        {order.delivery_address && (
          <a href={mapsUrl} target="_blank" rel="noreferrer" className="flex items-start gap-2 p-2.5 rounded-lg bg-sky-50 hover:bg-sky-100 transition-colors text-sm text-[#032B44]" data-testid={`courier-maps-${order.order_id}`}>
            <MapPin className="w-4 h-4 text-sky-700 mt-0.5 shrink-0" />
            <span className="flex-1">{order.delivery_address}</span>
          </a>
        )}
        {telUrl && (
          <a href={telUrl} className="flex items-center gap-2 p-2.5 rounded-lg bg-sky-50 hover:bg-sky-100 transition-colors text-sm text-[#032B44]" data-testid={`courier-call-${order.order_id}`}>
            <Phone className="w-4 h-4 text-sky-700 shrink-0" />
            <span className="font-mono">{order.user_phone}</span>
          </a>
        )}
        {order.notes && (
          <p className="text-xs text-slate-600 italic px-1">"{order.notes}"</p>
        )}
      </div>

      <button
        disabled={delivered || acting}
        onClick={() => onDelivered(order.order_id)}
        className={`mt-4 w-full h-11 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors disabled:cursor-not-allowed ${
          delivered
            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
            : "bg-[#032B44] hover:bg-[#00A3FF] text-white"
        }`}
        data-testid={`courier-delivered-${order.order_id}`}
      >
        {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
        {delivered ? "Consegnato" : isCash ? "Consegnato & incassato" : "Consegnato"}
      </button>
    </article>
  );
};

export default CourierSchedule;
